import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { SettingsApi } from '@/api';
import { Plus, Trash2, FileText, Sliders, GripVertical, Building2 } from 'lucide-react';

const TABS = [
  { id: 'general', label: 'General', icon: Sliders },
  { id: 'company', label: 'Company', icon: Building2 },
  { id: 'terms', label: 'Footer Terms', icon: FileText },
];

export default function Settings() {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    discount_review_threshold_pct: 0,
    default_pi_validity_days: 30,
    footer_terms_json: [] as { title: string; body: string }[],
    company_name: '',
    company_gstin: '',
    company_state: '',
    company_state_code: '',
    company_address1: '',
    company_address2: '',
    company_phone: '',
    company_email: '',
    company_website: '',
    bank_name: '',
    bank_account_name: '',
    bank_account_number: '',
    bank_ifsc: '',
    bank_branch: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDirty, setIsDirty] = useState(false);

  const settingsQuery = useQuery({
    queryKey: ['settings'],
    queryFn: () => SettingsApi.get(),
  });

  useEffect(() => {
    if (settingsQuery.data) {
      setFormData(settingsQuery.data);
      setIsDirty(false);
    }
  }, [settingsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => SettingsApi.update(data),
    onSuccess: (saved) => {
      // Refresh the cache with the saved row so Discard reverts to the latest
      // saved state (not the value loaded when the page first opened).
      if (saved) queryClient.setQueryData(['settings'], saved);
      push({ tone: 'success', title: 'Settings saved' });
      setIsDirty(false);
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to save settings', body: err.message });
    },
  });

  const validateForm = (): boolean => {
    const e: Record<string, string> = {};
    if (formData.discount_review_threshold_pct < 0 || formData.discount_review_threshold_pct > 100)
      e.discount = 'Must be between 0 and 100';
    if (formData.default_pi_validity_days <= 0)
      e.validity = 'Must be greater than 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    updateMutation.mutate(formData);
  };

  const handleReset = () => {
    if (!settingsQuery.data) return;
    setFormData(settingsQuery.data);
    setErrors({});
    setIsDirty(false);
  };

  const patch = (partial: Partial<typeof formData>) => {
    setFormData((f) => ({ ...f, ...partial }));
    setIsDirty(true);
  };

  const addTerm = () => {
    patch({ footer_terms_json: [...formData.footer_terms_json, { title: '', body: '' }] });
  };

  const removeTerm = (idx: number) => {
    patch({ footer_terms_json: formData.footer_terms_json.filter((_, i) => i !== idx) });
  };

  const updateTerm = (idx: number, field: 'title' | 'body', value: string) => {
    const terms = [...formData.footer_terms_json];
    terms[idx] = { ...terms[idx], [field]: value };
    patch({ footer_terms_json: terms });
  };

  const dragIdx = useRef<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const onDragStart = (idx: number) => { dragIdx.current = idx; };
  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOver(idx);
  };
  const onDrop = (targetIdx: number) => {
    const from = dragIdx.current;
    if (from === null || from === targetIdx) { setDragOver(null); return; }
    const terms = [...formData.footer_terms_json];
    const [moved] = terms.splice(from, 1);
    terms.splice(targetIdx, 0, moved);
    patch({ footer_terms_json: terms });
    dragIdx.current = null;
    setDragOver(null);
  };
  const onDragEnd = () => { dragIdx.current = null; setDragOver(null); };

  if (settingsQuery.isLoading) {
    return <div className="text-center py-20 text-muted">Loading…</div>;
  }

  return (
    <div className="flex flex-col md:h-[calc(100vh-48px)]">
      <PageHeader
        title="Settings"
        subtitle={isAdmin ? 'System-wide configuration for RDX PI Generator' : 'View-only — only admin can change these settings'}
      />

      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0 mt-5">
        {/* Sidebar tabs — horizontal scroll row on mobile, vertical rail on desktop */}
        <div className="w-full lg:w-44 shrink-0 lg:sticky lg:top-0">
          <nav className="flex lg:flex-col gap-1 lg:gap-0.5 lg:space-y-0.5 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 lg:w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left whitespace-nowrap ${
                    active
                      ? 'bg-rdx/8 text-rdx'
                      : 'text-muted hover:bg-slate-100 hover:text-ink'
                  }`}
                  style={active ? { backgroundColor: 'rgba(179,38,30,0.08)' } : undefined}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content panel */}
        <fieldset disabled={!isAdmin} className="contents">
        <div className="flex-1 min-w-0 overflow-y-auto pb-16">
          {activeTab === 'general' && (
            <div className="space-y-4">
              {/* PI Defaults */}
              <div className="card card-pad space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">PI Defaults</h3>
                  <p className="text-xs text-muted mt-0.5">Applied when creating new proforma invoices</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Default Validity (Days)</label>
                    <input
                      type="number"
                      className={`input mt-1 ${errors.validity ? 'input-error' : ''}`}
                      value={formData.default_pi_validity_days}
                      onChange={(e) => patch({ default_pi_validity_days: Number(e.target.value) })}
                      min={1}
                    />
                    {errors.validity
                      ? <p className="text-xs text-rdx mt-1">{errors.validity}</p>
                      : <p className="text-xs text-muted mt-1">How long a PI stays valid after issue</p>
                    }
                  </div>
                </div>
              </div>

              {/* Discount Policy */}
              <div className="card card-pad space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Discount Policy</h3>
                  <p className="text-xs text-muted mt-0.5">Controls when high-discount PIs are flagged for review</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Review Threshold (%)</label>
                    <div className="relative mt-1">
                      <input
                        type="number"
                        className={`input pr-8 ${errors.discount ? 'input-error' : ''}`}
                        value={formData.discount_review_threshold_pct}
                        onChange={(e) => patch({ discount_review_threshold_pct: Number(e.target.value) })}
                        min={0}
                        max={100}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">%</span>
                    </div>
                    {errors.discount
                      ? <p className="text-xs text-rdx mt-1">{errors.discount}</p>
                      : <p className="text-xs text-muted mt-1">PIs with any line discount above this appear in the dashboard watch list</p>
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'company' && (
            <div className="space-y-4">
              {/* Company Details */}
              <div className="card card-pad space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Company Details</h3>
                  <p className="text-xs text-muted mt-0.5">Appears on PDF letterhead and communications</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="label">Legal Name</label>
                    <input type="text" className="input mt-1" value={formData.company_name}
                      onChange={(e) => patch({ company_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">GSTIN</label>
                    <input type="text" className="input mt-1 font-mono" value={formData.company_gstin}
                      onChange={(e) => patch({ company_gstin: e.target.value.toUpperCase() })}
                      placeholder="e.g. 33AAACR0000Z1Z0" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="label">State</label>
                      <input type="text" className="input mt-1" value={formData.company_state}
                        onChange={(e) => patch({ company_state: e.target.value })} />
                    </div>
                    <div>
                      <label className="label">State Code</label>
                      <input type="text" className="input mt-1" value={formData.company_state_code}
                        onChange={(e) => patch({ company_state_code: e.target.value })}
                        placeholder="e.g. 33" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">Address Line 1</label>
                    <input type="text" className="input mt-1" value={formData.company_address1}
                      onChange={(e) => patch({ company_address1: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">Address Line 2</label>
                    <input type="text" className="input mt-1" value={formData.company_address2}
                      onChange={(e) => patch({ company_address2: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input type="text" className="input mt-1" value={formData.company_phone}
                      onChange={(e) => patch({ company_phone: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Email</label>
                    <input type="email" className="input mt-1" value={formData.company_email}
                      onChange={(e) => patch({ company_email: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">Website</label>
                    <input type="url" className="input mt-1" value={formData.company_website}
                      onChange={(e) => patch({ company_website: e.target.value })} />
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div className="card card-pad space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Bank Details</h3>
                  <p className="text-xs text-muted mt-0.5">Printed on PDF for payment reference</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">Bank Name</label>
                    <input type="text" className="input mt-1" value={formData.bank_name}
                      onChange={(e) => patch({ bank_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Branch</label>
                    <input type="text" className="input mt-1" value={formData.bank_branch}
                      onChange={(e) => patch({ bank_branch: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="label">Account Name</label>
                    <input type="text" className="input mt-1" value={formData.bank_account_name}
                      onChange={(e) => patch({ bank_account_name: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">Account Number</label>
                    <input type="text" className="input mt-1 font-mono" value={formData.bank_account_number}
                      onChange={(e) => patch({ bank_account_number: e.target.value })} />
                  </div>
                  <div>
                    <label className="label">IFSC</label>
                    <input type="text" className="input mt-1 font-mono" value={formData.bank_ifsc}
                      onChange={(e) => patch({ bank_ifsc: e.target.value.toUpperCase() })} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="card card-pad space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Footer Terms &amp; Conditions</h3>
                  <p className="text-xs text-muted mt-0.5">These sections appear at the bottom of every generated PDF</p>
                </div>
                <button className="btn btn-sm btn-secondary gap-1.5" onClick={addTerm}>
                  <Plus size={13} /> Add Section
                </button>
              </div>

              {formData.footer_terms_json.length === 0 ? (
                <div className="border-2 border-dashed border-line rounded-lg py-10 text-center">
                  <FileText size={28} className="mx-auto text-muted/40 mb-2" />
                  <p className="text-sm text-muted">No terms sections yet</p>
                  <button className="btn btn-sm btn-secondary mt-3 gap-1.5" onClick={addTerm}>
                    <Plus size={13} /> Add first section
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.footer_terms_json.map((term, idx) => (
                    <div
                      key={idx}
                      draggable
                      onDragStart={() => onDragStart(idx)}
                      onDragOver={(e) => onDragOver(e, idx)}
                      onDrop={() => onDrop(idx)}
                      onDragEnd={onDragEnd}
                      className={`border rounded-lg overflow-hidden transition-all ${
                        dragOver === idx ? 'border-rdx/50 shadow-md scale-[1.01]' : 'border-line'
                      }`}
                    >
                      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-line">
                        <div className="flex items-center gap-2">
                          <GripVertical size={13} className="text-muted/50 cursor-grab active:cursor-grabbing" />
                          <span className="text-xs font-medium text-muted">Section {idx + 1}</span>
                          {term.title && <span className="text-xs text-ink font-medium">- {term.title}</span>}
                        </div>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => removeTerm(idx)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                      <div className="p-3 space-y-3">
                        <div>
                          <label className="label">Title</label>
                          <input
                            type="text"
                            className="input mt-1 text-sm"
                            value={term.title}
                            onChange={(e) => updateTerm(idx, 'title', e.target.value)}
                            placeholder="e.g. Payment Terms"
                          />
                        </div>
                        <div>
                          <label className="label">Content</label>
                          <textarea
                            className="input mt-1 text-sm"
                            value={term.body}
                            onChange={(e) => updateTerm(idx, 'body', e.target.value)}
                            rows={3}
                            placeholder="Terms text…"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
        </fieldset>
      </div>

      {/* Sticky save bar */}
      {isDirty && isAdmin && (
        <div className="fixed bottom-0 left-0 md:left-60 right-0 z-40 flex items-center justify-between gap-3 px-4 sm:px-8 py-3 bg-white border-t border-amber-200 shadow-lg">
          <p className="text-xs text-amber-700 font-medium">You have unsaved changes</p>
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-secondary"
              onClick={handleReset}
              disabled={updateMutation.isPending}
            >
              Discard
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
